import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Divider, CircularProgress
} from "@mui/material";
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  AccessTime as TimeIcon
} from "@mui/icons-material";
import { db } from "../../../firebase"; 
import {
  collection, addDoc, getDocs, query, orderBy, doc, updateDoc, deleteDoc, Timestamp
} from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function Hostelatttiming() {
  const [timings, setTimings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    time: "21:00", // Default 09:00 PM in 24h format
  });

  useEffect(() => {
    fetchTimings();
  }, []);

  // Helper to convert 24h (13:00) to 12h (01:00 PM)
  const formatTo12Hr = (time24) => {
    if (!time24) return "";
    let [hours, minutes] = time24.split(':');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const fetchTimings = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "hostel_attendance_timings"), orderBy("createdAt", "asc"));
      const snap = await getDocs(q);
      setTimings(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error("Fetch Error:", err);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.time) {
      return Swal.fire("Error", "Please enter title and time", "error");
    }

    setLoading(true);
    try {
      const dataToSave = {
        title: form.title,
        timeValue: form.time, // "21:00"
        displayTime: formatTo12Hr(form.time), // "09:00 PM"
        updatedAt: Timestamp.now()
      };

      if (editId) {
        await updateDoc(doc(db, "hostel_attendance_timings", editId), dataToSave);
        Swal.fire("Updated", "Timing updated successfully", "success");
      } else {
        await addDoc(collection(db, "hostel_attendance_timings"), {
          ...dataToSave,
          createdAt: Timestamp.now()
        });
        Swal.fire("Saved", "New timing slot added", "success");
      }
      setOpen(false);
      resetForm();
      fetchTimings();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Delete this attendance timing slot?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33"
    });

    if (confirm.isConfirmed) {
      await deleteDoc(doc(db, "hostel_attendance_timings", id));
      fetchTimings();
      Swal.fire("Deleted", "Record removed", "success");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({
      title: row.title,
      time: row.timeValue
    });
    setOpen(true);
  };

  const resetForm = () => {
    setEditId(null);
    setForm({ title: "", time: "21:00" });
  };

  return (
    <Box p={1} sx={{ background: "#f8fafc", minHeight: "100vh" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            Hostel Attendance Timings
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Manage scheduled roll-call slots
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: "#0f766e" } }} 
          onClick={() => { resetForm(); setOpen(true); }}
        >
          Add Slot
        </Button>
      </Stack>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Table>
          <TableHead sx={{ bgcolor: "#f1f5f9" }}>
            <TableRow>
              <TableCell width="80px"><b>Sr. No</b></TableCell>
              <TableCell><b>Attendance Title</b></TableCell>
              <TableCell><b>Scheduled Time</b></TableCell>
              <TableCell align="right"><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={30} sx={{ color: PRIMARY }} />
                </TableCell>
              </TableRow>
            ) : timings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No timings defined yet.
                </TableCell>
              </TableRow>
            ) : (
              timings.map((row, index) => (
                <TableRow key={row.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography fontWeight="500">{row.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimeIcon fontSize="small" sx={{ color: PRIMARY }} />
                      <Typography fontWeight="bold">{row.displayTime}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleEdit(row)} color="primary" size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(row.id)} color="error" size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Entry Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {editId ? "Update Timing Slot" : "Add New Timing Slot"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <TextField
              label="Attendance Title"
              placeholder="e.g. Night Attendance"
              fullWidth
              size="small"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />

            <TextField
              label="Select Time"
              type="time"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
            />

            <Button
              variant="contained"
              fullWidth
              sx={{ py: 1.2, bgcolor: PRIMARY, mt: 2 }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Processing..." : editId ? "Update Settings" : "Save Timing"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Hostelatttiming;